import { scopeConfidence } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';

/**
 * The role + scope claim readout.
 *
 * CLM-17 requires both to be surfaced, and requires them surfaced *separately*,
 * because they are not the same thing and treating them as one is how people
 * end up believing a Legal reviewer can see every client:
 *
 *   ROLE  (`roles` claim)  — which actions are permitted.
 *   SCOPE (`groups` claim) — which parties are visible.
 *
 * Everything shown here is informational. NFR-06 puts the decision in the
 * gateway API, which re-derives both from the bearer token on every request and
 * applies scope as a query predicate. If this panel and the API ever disagree,
 * the API is right and this panel is stale.
 */
export function ClaimsCard() {
  const { identity, isAuthenticated } = useIdentity();
  const confidence = scopeConfidence(identity, isAuthenticated);

  return (
    <div className="card claims">
      <h3>Your access</h3>
      <dl>
        <dt>Signed in as</dt>
        <dd>
          {identity.display_name ?? '—'}
          {identity.username ? <span className="mut"> · {identity.username}</span> : null}
        </dd>

        <dt>Directory</dt>
        <dd className="mut">
          tenant {identity.tenant_id ?? '—'} · object id {identity.object_id ?? '—'}
        </dd>

        <dt>Role claims</dt>
        <dd>
          {identity.roles.length > 0 ? (
            <span className="fl">
              {identity.roles.map((role) => (
                <span className="tag ai" key={role}>
                  {role}
                </span>
              ))}
            </span>
          ) : (
            <span className="mut">
              No <code>roles</code> claim in the token. Either no app role is assigned to you on the
              CLM app registration, or none is configured yet.
            </span>
          )}
        </dd>

        {/* "Groups", not "Scope groups": this lists every group in the token —
            role groups and unrelated ones beside any CEQ-CLM-Scope-* group —
            and the SPA cannot tell which is which (TopBar's chip, 30 Sep 2026). */}
        <dt>Groups in your sign-in</dt>
        <dd>
          {confidence === 'scoped' ? (
            <>
              <span className="fl">
                {identity.scope_groups.map((group) => (
                  <span className="tag out" key={group}>
                    {group}
                  </span>
                ))}
              </span>
              {/*
                Entra emits group *object ids* unless the app registration is
                configured to emit names for synced groups, so these are often
                GUIDs. Turning them into readable practice / client names needs
                the directory, which the SPA must not query directly (rule 11)
                — the API will return resolved names alongside effective access
                in a later story (PR-11, vw_effective_access).
              */}
              <p className="mut">
                Group identifiers as issued by Entra. Your CLM scope comes from the CEQ-CLM-Scope-*
                groups among them, which the API resolves; this page cannot tell which those are.
                Readable practice and client names come from the API once effective-access
                resolution lands.
              </p>
            </>
          ) : confidence === 'none' ? (
            /* Not "queries return nothing": the API refuses every call for this
               token — `no_groups_claim` when the claim is absent, `no_clm_role_group`
               when it is present but empty — and a scope group alone fixes
               neither (packages/auth authorization.ts). */
            <span className="mut">
              No groups in your sign-in. The API refuses every call until your sign-in carries
              both a CLM role group and a CLM scope group (CEQ-CLM-Scope-*). If you are in groups
              and none appear here, the app registration may not be configured to send the{' '}
              <code>groups</code> claim. This is the NFR-05 group-hygiene case, owned by the PMO
              Director.
            </span>
          ) : (
            <span className="mut">
              Entra omitted the group list from this token because it exceeded the claim size limit.
              Your scope is intact — the API resolves it server-side. It simply cannot be displayed
              from the token alone.
            </span>
          )}
        </dd>
      </dl>
      <p className="mut">
        Shown for transparency. Authorisation is enforced by the API, not by this interface — what
        you can actually do is decided server-side on every request (NFR-06).
      </p>
    </div>
  );
}
